import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { MessageSquare, Send, Paperclip, X, FileText, Search, MessageSquareText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const [params] = useSearchParams();
  // Search state
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchHits, setSearchHits] = useState(null);
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ title: "", body: "" });

  const loadThreads = async () => {
    try {
      const r = await api.get("/threads");
      setThreads(r.data.items || []);
      // Auto-open thread from ?thread= query
      const initial = params.get("thread");
      if (initial && !active) {
        const t = (r.data.items || []).find((x) => x.id === initial);
        if (t) openThread(t);
      }
    } catch { /* silent */ }
  };

  const openThread = async (t) => {
    setActive(t);
    try {
      const r = await api.get(`/threads/${t.id}/messages`);
      setMessages(r.data.items || []);
      await loadThreads();
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadThreads();
    const id = setInterval(() => {
      loadThreads();
      if (active) {
        api.get(`/threads/${active.id}/messages`).then((r) => setMessages(r.data.items || [])).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const uploadAttachment = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/messages/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [...prev, r.data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attachment failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async () => {
    if (!active) return;
    if (!draft.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      await api.post(`/threads/${active.id}/messages`, {
        body: draft, attachments, refs: [],
      });
      setDraft("");
      setAttachments([]);
      const r = await api.get(`/threads/${active.id}/messages`);
      setMessages(r.data.items || []);
      loadThreads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) {
      setSearchHits(null);
      return;
    }
    setSearching(true);
    try {
      const r = await api.get("/messages/search", { params: { q, limit: 40 } });
      setSearchHits(r.data.items || []);
    } catch (err) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };
  const clearSearch = () => { setQuery(""); setSearchHits(null); };

  const loadTemplates = async () => {
    try {
      const r = await api.get("/message-templates");
      setTemplates(r.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadTemplates(); }, []);
  const applyTemplate = (tpl) => {
    setDraft((prev) => prev ? `${prev}\n\n${tpl.body}` : tpl.body);
    setTplOpen(false);
  };
  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!newTpl.title.trim() || !newTpl.body.trim()) {
      toast.error("Title and body required"); return;
    }
    try {
      await api.post("/message-templates", newTpl);
      setNewTpl({ title: "", body: "" });
      toast.success("Template saved");
      await loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };
  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/message-templates/${id}`);
      await loadTemplates();
    } catch { toast.error("Delete failed"); }
  };
  const jumpToHit = async (hit) => {
    // Open the thread the hit belongs to
    const t = threads.find((x) => x.id === hit.thread_id);
    if (t) {
      await openThread(t);
    } else {
      // Refresh threads then open
      await loadThreads();
      const r = await api.get("/threads");
      const t2 = (r.data.items || []).find((x) => x.id === hit.thread_id);
      if (t2) await openThread(t2);
    }
    clearSearch();
  };

  return (
    <>
      <PageHeader
        title="Messages"
        subtitle="Client threads"
        testid="messages-page-header"
      />
      <PageBody>
        <div
          className="grid gap-0 border overflow-hidden bg-white"
          style={{ borderColor: "var(--ll-border)", gridTemplateColumns: "300px 1fr", minHeight: "60vh" }}
          data-testid="messages-page"
        >
          {/* Thread list */}
          <div className="border-r overflow-y-auto" style={{ borderColor: "var(--ll-border)" }}>
            <form onSubmit={runSearch} className="px-3 py-2 border-b flex items-center gap-1" style={{ borderColor: "var(--ll-border)" }}>
              <div className="relative flex-1">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ll-ink-muted)]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search messages…"
                  data-testid="messages-search-input"
                  className="w-full pl-7 pr-6 py-1.5 border text-xs bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                />
                {query && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label="Clear"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)]"
                  ><X size={12} /></button>
                )}
              </div>
            </form>
            {searchHits !== null ? (
              <div data-testid="messages-search-results">
                <div className="px-4 py-2 bg-[#0b5d9e] text-[10px] uppercase tracking-widest text-white flex justify-between" style={{ borderColor: "var(--ll-border)" }}>
                  <span>{searching ? "Searching…" : `${searchHits.length} match${searchHits.length === 1 ? "" : "es"}`}</span>
                  <button onClick={clearSearch} className="hover:text-white/80" data-testid="messages-search-clear">Clear</button>
                </div>
                {searchHits.length === 0 && !searching && (
                  <div className="px-5 py-6 text-center text-xs text-[var(--ll-ink-soft)]">No matches. Try another keyword.</div>
                )}
                {searchHits.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => jumpToHit(h)}
                    data-testid={`search-hit-${h.id}`}
                    className="w-full text-left px-4 py-3 border-b hover:bg-[var(--ll-bg-sidebar)]"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-medium truncate">{h.client_name || h.client_email}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{h.sender_role}</div>
                    </div>
                    <div className="text-xs text-[var(--ll-ink-soft)] mt-1 line-clamp-2">{h.snippet}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1">{fmtDate(h.created_at)}</div>
                  </button>
                ))}
              </div>
            ) : (
              <>
            <div className="px-4 py-3 border-b bg-[#0b5d9e] text-xs uppercase tracking-widest text-white" style={{ borderColor: "var(--ll-border)" }}>
              {threads.length} client{threads.length === 1 ? "" : "s"}
            </div>
            {threads.length === 0 ? (
              <div className="p-6 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="threads-empty">
                No client threads yet. Threads appear automatically when a client sends their first message.
              </div>
            ) : (
              threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => openThread(t)}
                  data-testid={`thread-row-${t.id}`}
                  className={`w-full text-left px-4 py-3 border-b hover:bg-[var(--ll-bg-sidebar)] ${active?.id === t.id ? "bg-[var(--ll-mint)]" : ""}`}
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{t.client_name || t.client_email || "Client"}</div>
                    {t.unread_for_staff > 0 && (
                      <span
                        className="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold grid place-items-center"
                        style={{ background: "var(--ll-ochre)" }}
                        data-testid={`thread-unread-${t.id}`}
                      >
                        {t.unread_for_staff}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--ll-ink-soft)] truncate mt-0.5">{t.last_message_preview || "No messages yet"}</div>
                  {t.last_message_at && (
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1">{fmtDate(t.last_message_at)}</div>
                  )}
                </button>
              ))
            )}
              </>
            )}
          </div>

          {/* Active conversation */}
          <div className="flex flex-col min-h-0">
            {!active ? (
              <div className="flex-1 grid place-items-center text-sm text-[var(--ll-ink-soft)]" data-testid="threads-placeholder">
                <div className="text-center">
                  <MessageSquare size={28} className="mx-auto mb-3 opacity-60" />
                  Pick a client to open the conversation.
                </div>
              </div>
            ) : (
              <>
                <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
                  <div className="font-display font-bold">{active.client_name || active.client_email}</div>
                  <div className="text-xs text-[var(--ll-ink-soft)]">{active.client_email}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-conversation">
                  {messages.length === 0 ? (
                    <div className="text-center text-sm text-[var(--ll-ink-soft)] py-10">
                      No messages yet. Say hi.
                    </div>
                  ) : (
                    messages.map((m, mi) => {
                      const isStaff = m.sender_role === "staff";
                      // Only show "Seen" on the *last* staff outgoing that's been read.
                      const isLastReadOutgoing = isStaff && m.read_by_client && !messages.slice(mi + 1).some((n) => n.sender_role === "staff");
                      return (
                        <div key={m.id} className={`flex flex-col ${isStaff ? "items-end" : "items-start"}`}>
                          <div
                            className="max-w-[70%] px-3 py-2 text-sm"
                            style={{
                              background: isStaff ? "var(--ll-ink)" : "var(--ll-mint)",
                              color: isStaff ? "white" : "var(--ll-ink)",
                            }}
                          >
                            <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">
                              {isStaff ? m.sender_name || "You" : m.sender_name || "Client"} · {fmtDate(m.created_at)}
                            </div>
                            {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                            {m.attachments?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {m.attachments.map((a, i) => (
                                  <a
                                    key={i}
                                    href={`${api.defaults.baseURL || ""}/messages/attachments/download?path=${encodeURIComponent(a.storage_path)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-[11px] flex items-center gap-1 underline"
                                    data-testid={`msg-att-${m.id}-${i}`}
                                  >
                                    <Paperclip size={11} /> {a.file_name}
                                  </a>
                                ))}
                              </div>
                            )}
                            {m.refs?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {m.refs.map((r, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border"
                                    style={{ borderColor: isStaff ? "rgba(255,255,255,0.3)" : "var(--ll-border)" }}
                                  >
                                    <FileText size={10} />{r.label || r.kind}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {isLastReadOutgoing && (
                            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-1" data-testid={`msg-seen-${m.id}`}>
                              Seen {fmtDate(m.read_by_client_at)}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {attachments.length > 0 && (
                  <div className="px-4 py-2 border-t text-xs flex flex-wrap gap-1" style={{ borderColor: "var(--ll-border)" }} data-testid="msg-attach-preview">
                    {attachments.map((a, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--ll-mint)]">
                        <Paperclip size={10} /> {a.file_name}
                        <button onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} aria-label="Remove"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="p-3 border-t flex items-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
                  <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="msg-attach-btn"
                    className="p-2 border hover:bg-[var(--ll-mint)] shrink-0"
                    style={{ borderColor: "var(--ll-border)" }}
                    aria-label="Attach file"
                  >
                    <Paperclip size={14} />
                  </button>
                  <button
                    onClick={() => setTplOpen(true)}
                    data-testid="msg-templates-btn"
                    className="p-2 border hover:bg-[var(--ll-mint)] shrink-0"
                    style={{ borderColor: "var(--ll-border)" }}
                    aria-label="Insert template"
                    title="Templates"
                  >
                    <MessageSquareText size={14} />
                  </button>
                  <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadAttachment(e.target.files?.[0])} />
                  <textarea
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                    placeholder="Type a reply… ⌘/Ctrl+Enter to send"
                    data-testid="msg-input"
                    className="flex-1 px-3 py-2 border text-sm resize-none"
                    style={{ borderColor: "var(--ll-border)" }}
                  />
                  <button
                    onClick={send}
                    disabled={sending || (!draft.trim() && attachments.length === 0)}
                    data-testid="msg-send"
                    className="p-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] disabled:opacity-50 shrink-0"
                    aria-label="Send"
                  >
                    <Send size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </PageBody>

      {tplOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          onClick={() => setTplOpen(false)}
          data-testid="msg-templates-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto"
            style={{ borderColor: "var(--ll-ink)" }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-2xl flex items-center gap-2">
                  <MessageSquareText size={18} /> Message templates
                </h2>
                <p className="text-sm text-[var(--ll-ink-soft)] mt-1">
                  Save canned replies for your team. Click any template to insert it into your draft.
                </p>
              </div>
              <button
                onClick={() => setTplOpen(false)}
                aria-label="Close"
                data-testid="msg-templates-close"
                className="text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"
              >
                <X size={16} />
              </button>
            </div>

            {templates.length === 0 ? (
              <div className="text-sm text-[var(--ll-ink-soft)] py-4 text-center border" style={{ borderColor: "var(--ll-border)" }}>
                No templates yet — create one below.
              </div>
            ) : (
              <div className="space-y-1 mb-4" data-testid="msg-templates-list">
                {templates.map((t) => (
                  <div
                    key={t.id}
                    className="border p-3 flex items-start gap-3 hover:bg-[var(--ll-bg-sidebar)]"
                    style={{ borderColor: "var(--ll-border)" }}
                    data-testid={`tpl-row-${t.id}`}
                  >
                    <button onClick={() => applyTemplate(t)} className="flex-1 text-left" data-testid={`tpl-apply-${t.id}`}>
                      <div className="text-sm font-medium">{t.title}</div>
                      <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5 line-clamp-2">{t.body}</div>
                    </button>
                    <button
                      onClick={() => deleteTemplate(t.id)}
                      data-testid={`tpl-delete-${t.id}`}
                      aria-label="Delete template"
                      className="p-1.5 text-[var(--ll-ink-muted)] hover:text-[#DC2626]"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={saveTemplate} className="border-t pt-4" style={{ borderColor: "var(--ll-border)" }} data-testid="tpl-new-form">
              <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">New template</div>
              <input
                required
                value={newTpl.title}
                onChange={(e) => setNewTpl({ ...newTpl, title: e.target.value })}
                placeholder="Title (e.g. Please resend statement)"
                maxLength={80}
                data-testid="tpl-new-title"
                className="w-full px-3 py-2 border text-sm mb-2"
                style={{ borderColor: "var(--ll-border)" }}
              />
              <textarea
                required
                rows={3}
                value={newTpl.body}
                onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })}
                placeholder="Body — the canned reply text…"
                maxLength={2000}
                data-testid="tpl-new-body"
                className="w-full px-3 py-2 border text-sm mb-2"
                style={{ borderColor: "var(--ll-border)" }}
              />
              <button
                type="submit"
                data-testid="tpl-new-submit"
                className="text-sm px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center gap-1.5"
              >
                <Plus size={12} /> Save template
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
